import { StyledForm, StyledFormAction, StyledFormBody } from '@/pages/workgroup/form/setting/styled';
import { AdvancedType, Advanced } from '@/pages/workgroup/form/WorkSpaceSubForm';
import { useMutationUpdateWorkspace } from '@/pages/workgroup/hook';
import { UpdateWorkspaceRequest, WorkspaceT, WorkSpaceType } from '@/pages/workgroup/type';
import { getErrorMessage } from '@/utils/utils';
import {
  AUTO_STOP_DISABLED_MINUTES,
  DEFAULT_AUTO_STOP_MINUTES,
  DEFAULT_MAINTENANCE_WINDOW_DAY_OF_WEEK,
  DEFAULT_MAINTENANCE_WINDOW_HOUR_OF_DAY,
  DEFAULT_MAINTENANCE_WINDOW_INTERVAL_WEEKS,
} from '@/pages/workgroup/form/setting/constants';
import { Button } from '@tigergraph/app-ui-lib/button';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ScheduleResizeHandle } from '@/pages/workgroup/form/setting/schedule';
import { useRef, useState } from 'react';
import { AxiosError } from 'axios';

export default function AdvancedForm({
  workspace,
  onClose,
  workspaceTypes,
}: {
  workspace: WorkspaceT;
  onClose: () => void;
  workspaceTypes: WorkSpaceType[];
}) {
  const form = useForm<AdvancedType>({
    defaultValues: {
      autoSuspend: workspace.auto_stop_minutes > AUTO_STOP_DISABLED_MINUTES,
      auto_stop_minutes: workspace.auto_stop_minutes || DEFAULT_AUTO_STOP_MINUTES,
      enable_auto_start: workspace.enable_auto_start,

      update_strategy: workspace.update_strategy,
      desired_tg_version: workspace.desired_tg_version,
      auto_upgrade_state: workspace.auto_upgrade_state,

      maintenance_window_hour_of_day:
        workspace.maintenance_window_hour_of_day ?? DEFAULT_MAINTENANCE_WINDOW_HOUR_OF_DAY,
      maintenance_window_day_of_week:
        workspace.maintenance_window_day_of_week ?? DEFAULT_MAINTENANCE_WINDOW_DAY_OF_WEEK,
      maintenance_window_time_zone:
        workspace.maintenance_window_time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      maintenance_window_interval_weeks:
        workspace.maintenance_window_interval_weeks ?? DEFAULT_MAINTENANCE_WINDOW_INTERVAL_WEEKS,
    },
  });

  const { handleSubmit, watch } = form;

  const autoSuspend = watch('autoSuspend');
  const auto_stop_minutes = watch('auto_stop_minutes');
  const enable_auto_start = watch('enable_auto_start');

  const update_strategy = watch('update_strategy');
  const desired_tg_version = watch('desired_tg_version');
  const auto_upgrade_state = watch('auto_upgrade_state');

  const maintenance_window_hour_of_day = watch('maintenance_window_hour_of_day');
  const maintenance_window_day_of_week = watch('maintenance_window_day_of_week');
  const maintenance_window_time_zone = watch('maintenance_window_time_zone');
  const maintenance_window_interval_weeks = watch('maintenance_window_interval_weeks');

  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const isDirty =
    autoSuspend !== workspace.auto_stop_minutes > AUTO_STOP_DISABLED_MINUTES ||
    Number(auto_stop_minutes) !== (workspace.auto_stop_minutes || DEFAULT_AUTO_STOP_MINUTES) ||
    enable_auto_start !== workspace.enable_auto_start ||
    update_strategy !== workspace.update_strategy ||
    desired_tg_version !== workspace.desired_tg_version ||
    auto_upgrade_state !== workspace.auto_upgrade_state ||
    maintenance_window_hour_of_day !==
      (workspace.maintenance_window_hour_of_day ?? DEFAULT_MAINTENANCE_WINDOW_HOUR_OF_DAY) ||
    maintenance_window_day_of_week !==
      (workspace.maintenance_window_day_of_week ?? DEFAULT_MAINTENANCE_WINDOW_DAY_OF_WEEK) ||
    maintenance_window_time_zone !==
      (workspace.maintenance_window_time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone) ||
    maintenance_window_interval_weeks !==
      (workspace.maintenance_window_interval_weeks ?? DEFAULT_MAINTENANCE_WINDOW_INTERVAL_WEEKS);

  // Only allow saving when the maintenance interval is a whole number in 1-52.
  const isIntervalValid =
    typeof maintenance_window_interval_weeks === 'number' &&
    Number.isInteger(maintenance_window_interval_weeks) &&
    maintenance_window_interval_weeks >= 1 &&
    maintenance_window_interval_weeks <= 52;

  const updateWorkSpaceMutation = useMutationUpdateWorkspace();

  const scheduleRef = useRef<ScheduleResizeHandle>(null);

  const onUpdate = async (data: AdvancedType) => {
    setSaving(true);
    let toastId: string | undefined;
    try {
      toastId = toast.loading('Updating workspace');
      // Save schedule first
      if (scheduleRef.current && scheduleDirty) {
        await scheduleRef.current.save();
      }
      // Save other workspace settings
      const { autoSuspend, auto_stop_minutes, enable_auto_start } = data;
      const updateRequest: UpdateWorkspaceRequest = {
        enable_auto_start,
        auto_stop_minutes: autoSuspend ? Number(auto_stop_minutes) : AUTO_STOP_DISABLED_MINUTES,

        update_strategy: data.update_strategy,
        desired_tg_version: data.desired_tg_version,
        auto_upgrade_state: data.auto_upgrade_state,

        maintenance_window_hour_of_day: data.maintenance_window_hour_of_day,
        maintenance_window_day_of_week: data.maintenance_window_day_of_week,
        maintenance_window_time_zone: data.maintenance_window_time_zone,
        maintenance_window_interval_weeks: data.maintenance_window_interval_weeks,
      };
      await updateWorkSpaceMutation.mutateAsync(
        {
          group_id: workspace.workgroup_id,
          space_id: workspace.workspace_id,
          org_id: workspace.org_id,
          data: updateRequest,
        },
        {
          onSuccess() {
            toast.success('Workspace updating request accepted.', { id: toastId });
            onClose();
          },
          onError(err) {
            toast.error(`${getErrorMessage(err)}`, { id: toastId });
          },
        }
      );
    } catch (err) {
      if (err instanceof AxiosError) {
        toast.error(`${getErrorMessage(err)}`, { id: toastId });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledForm>
      <StyledFormBody>
        <Advanced
          form={form}
          workspace={workspace}
          workspaceTypes={workspaceTypes}
          scheduleRef={scheduleRef}
          onDirtyChange={setScheduleDirty}
          onClose={onClose}
        />
      </StyledFormBody>
      <StyledFormAction>
        <Button type="button" kind="secondary" size="large" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="large"
          disabled={(!isDirty && !scheduleDirty) || !isIntervalValid}
          isLoading={saving}
          onClick={handleSubmit(async (data) => {
            await onUpdate(data);
          })}
        >
          Save
        </Button>
      </StyledFormAction>
    </StyledForm>
  );
}
